import { create } from 'zustand';
import { nanoid } from 'nanoid';
import type {
  BulkExportJob,
  BulkExportJobStatus,
  BulkExportTarget,
  BulkExportItemProgress,
  FilenamePattern,
  ExportPreset,
  BulkItemStatus,
  ExportConfig,
  JPEGQuality,
} from '../types/export';
import {
  DEFAULT_FILENAME_PATTERN,
  EXPORT_PRESETS,
  generatePacketFilename,
  getScaleSuffix,
  UNBRANDED_LEGACY_ID,
} from '../types/export';
import { resolveElementsForBrand, fetchMappingSourceData } from '../lib/brandResolver';
import { runExportPipeline } from '../lib/exportEngine';
import { useStudioStore } from './studioStore';
import { useMappingStore } from './mappingStore';
import { useExportStore } from './exportStore';
import { supabase } from '../lib/supabase';

const BULK_BUCKET = 'cs-bulk-exports';
const CUSTOM_PRESET_PREFIX = 'custom:';

interface BulkExportState {
  job: BulkExportJob | null;
  selectedPreset: ExportPreset;
  filenamePattern: FilenamePattern;
  selectedTargets: BulkExportTarget[];
  cancelRequested: boolean;
  pauseRequested: boolean;
  storeId: string;

  qualityOverride: JPEGQuality | null;
  scaleOverride: number | null;

  customPresets: ExportPreset[];
  customPresetsLoaded: boolean;
  prefsLoaded: boolean;

  setSelectedPreset: (preset: ExportPreset) => void;
  setFilenamePattern: (pattern: FilenamePattern) => void;
  setSelectedTargets: (targets: BulkExportTarget[]) => void;
  setStoreId: (storeId: string) => void;
  addTarget: (target: BulkExportTarget) => void;
  removeTarget: (brandId: string) => void;
  clearTargets: () => void;

  setQualityOverride: (quality: JPEGQuality | null) => void;
  setScaleOverride: (scale: number | null) => void;
  resetOverrides: () => void;
  getEffectiveConfig: () => ExportConfig;

  loadUserPrefs: () => Promise<void>;
  persistUserPrefs: () => Promise<void>;

  loadCustomPresets: () => Promise<void>;
  saveCustomPreset: (label: string) => Promise<ExportPreset | null>;
  deleteCustomPreset: (presetId: string) => Promise<void>;

  startBulkExport: () => Promise<void>;
  pauseExport: () => void;
  resumeExport: () => void;
  cancelExport: () => void;
  reset: () => void;
}

function mergeEffectiveConfig(
  preset: ExportPreset,
  qualityOverride: JPEGQuality | null,
  scaleOverride: number | null
): ExportConfig {
  return {
    ...preset.config,
    quality: qualityOverride ?? preset.config.quality,
    scale: scaleOverride ?? preset.config.scale,
  };
}

function customPresetFromRow(row: {
  id: string;
  label: string;
  base_preset_id: string;
  config: ExportConfig;
}): ExportPreset {
  return {
    id: `${CUSTOM_PRESET_PREFIX}${row.id}`,
    label: row.label,
    description: `Custom preset based on ${row.base_preset_id}`,
    config: row.config,
  };
}

export const useBulkExportStore = create<BulkExportState>((set, get) => ({
  job: null,
  selectedPreset: EXPORT_PRESETS[0],
  filenamePattern: { ...DEFAULT_FILENAME_PATTERN },
  selectedTargets: [],
  cancelRequested: false,
  pauseRequested: false,
  storeId: '0',

  qualityOverride: null,
  scaleOverride: null,

  customPresets: [],
  customPresetsLoaded: false,
  prefsLoaded: false,

  setSelectedPreset: (preset) => set({ selectedPreset: preset }),

  setFilenamePattern: (pattern) => set({ filenamePattern: pattern }),

  setStoreId: (storeId) => set({ storeId: storeId || '0' }),

  setSelectedTargets: (targets) => set({ selectedTargets: targets }),

  addTarget: (target) => {
    set((state) => {
      if (state.selectedTargets.some((t) => t.brandId === target.brandId)) return state;
      return { selectedTargets: [...state.selectedTargets, target] };
    });
  },

  removeTarget: (brandId) => {
    set((state) => ({
      selectedTargets: state.selectedTargets.filter((t) => t.brandId !== brandId),
    }));
  },

  clearTargets: () => set({ selectedTargets: [] }),

  setQualityOverride: (quality) => set({ qualityOverride: quality }),
  setScaleOverride: (scale) => set({ scaleOverride: scale }),
  resetOverrides: () => set({ qualityOverride: null, scaleOverride: null }),

  getEffectiveConfig: () => {
    const { selectedPreset, qualityOverride, scaleOverride } = get();
    return mergeEffectiveConfig(selectedPreset, qualityOverride, scaleOverride);
  },

  loadUserPrefs: async () => {
    if (get().prefsLoaded) return;
    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;
      if (!userId) {
        set({ prefsLoaded: true });
        return;
      }
      const { data } = await supabase
        .from('cs_user_export_prefs')
        .select('last_preset_id, last_quality, last_scale, last_store_id')
        .eq('user_id', userId)
        .maybeSingle();

      if (data) {
        const { customPresets } = get();
        const allPresets = [...EXPORT_PRESETS, ...customPresets];
        const matched = allPresets.find((p) => p.id === data.last_preset_id);
        set({
          selectedPreset: matched ?? EXPORT_PRESETS[0],
          qualityOverride: (data.last_quality as JPEGQuality) ?? null,
          scaleOverride: typeof data.last_scale === 'number' ? data.last_scale : Number(data.last_scale) || null,
          storeId: data.last_store_id || '0',
          prefsLoaded: true,
        });
      } else {
        set({ prefsLoaded: true });
      }
    } catch (err) {
      console.error('[BulkExport] Failed to load user prefs:', err);
      set({ prefsLoaded: true });
    }
  },

  persistUserPrefs: async () => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;
      if (!userId) return;
      const { selectedPreset, qualityOverride, scaleOverride, storeId } = get();
      const effective = mergeEffectiveConfig(selectedPreset, qualityOverride, scaleOverride);
      await supabase
        .from('cs_user_export_prefs')
        .upsert(
          {
            user_id: userId,
            last_preset_id: selectedPreset.id,
            last_quality: effective.quality,
            last_scale: effective.scale,
            last_store_id: storeId,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id' }
        );
    } catch (err) {
      console.error('[BulkExport] Failed to persist user prefs:', err);
    }
  },

  loadCustomPresets: async () => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;
      if (!userId) {
        set({ customPresetsLoaded: true });
        return;
      }
      const { data } = await supabase
        .from('cs_custom_export_presets')
        .select('id, label, base_preset_id, config')
        .eq('user_id', userId)
        .order('created_at', { ascending: true });

      const presets = (data ?? []).map(customPresetFromRow);
      set({ customPresets: presets, customPresetsLoaded: true });
    } catch (err) {
      console.error('[BulkExport] Failed to load custom presets:', err);
      set({ customPresetsLoaded: true });
    }
  },

  saveCustomPreset: async (label) => {
    const trimmed = label.trim();
    if (!trimmed) return null;
    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;
      if (!userId) return null;

      const { selectedPreset, qualityOverride, scaleOverride } = get();
      const basePresetId = selectedPreset.id.startsWith(CUSTOM_PRESET_PREFIX)
        ? 'web-hero'
        : selectedPreset.id;
      const config = mergeEffectiveConfig(selectedPreset, qualityOverride, scaleOverride);

      const { data, error } = await supabase
        .from('cs_custom_export_presets')
        .insert({
          user_id: userId,
          label: trimmed,
          base_preset_id: basePresetId,
          config,
        })
        .select('id, label, base_preset_id, config')
        .maybeSingle();

      if (error || !data) {
        console.error('[BulkExport] Failed to save custom preset:', error);
        return null;
      }

      const preset = customPresetFromRow(data);
      set((s) => ({
        customPresets: [...s.customPresets, preset],
        selectedPreset: preset,
        qualityOverride: null,
        scaleOverride: null,
      }));
      return preset;
    } catch (err) {
      console.error('[BulkExport] Failed to save custom preset:', err);
      return null;
    }
  },

  deleteCustomPreset: async (presetId) => {
    if (!presetId.startsWith(CUSTOM_PRESET_PREFIX)) return;
    const rowId = presetId.slice(CUSTOM_PRESET_PREFIX.length);
    try {
      await supabase.from('cs_custom_export_presets').delete().eq('id', rowId);
      set((s) => {
        const next = s.customPresets.filter((p) => p.id !== presetId);
        const isSelected = s.selectedPreset.id === presetId;
        return {
          customPresets: next,
          selectedPreset: isSelected ? EXPORT_PRESETS[0] : s.selectedPreset,
        };
      });
    } catch (err) {
      console.error('[BulkExport] Failed to delete custom preset:', err);
    }
  },

  startBulkExport: async () => {
    const { selectedTargets, selectedPreset, filenamePattern, storeId, qualityOverride, scaleOverride } = get();
    if (selectedTargets.length === 0) return;

    const { activeProject, canvasState, schools } = useStudioStore.getState();
    const { elementMappings } = useMappingStore.getState();
    const { buttonUrls } = useExportStore.getState();
    if (!activeProject) return;

    const jobId = nanoid(12);
    const config = mergeEffectiveConfig(selectedPreset, qualityOverride, scaleOverride);

    const items: BulkExportItemProgress[] = selectedTargets.map((t) => ({
      brandId: t.brandId,
      status: 'pending' as BulkItemStatus,
      percent: 0,
      message: 'Waiting...',
    }));

    const job: BulkExportJob = {
      id: jobId,
      projectId: activeProject.id,
      presetId: selectedPreset.id,
      config,
      filenamePattern,
      targets: [...selectedTargets],
      items,
      status: 'running',
      startedAt: new Date(),
      completedAt: null,
      totalCount: selectedTargets.length,
      completedCount: 0,
      failedCount: 0,
    };

    set({ job, cancelRequested: false, pauseRequested: false });

    // Persist latest prefs on export kickoff (fire-and-forget)
    get().persistUserPrefs().catch(() => {});

    const zipFiles: { name: string; blob: Blob }[] = [];
    const sourceDataCacheByBrand: Record<string, Record<string, Record<string, unknown>>> = {};

    for (let i = 0; i < selectedTargets.length; i++) {
      const currentState = get();
      if (currentState.cancelRequested) {
        set((s) => ({
          job: s.job ? { ...s.job, status: 'cancelled' as BulkExportJobStatus, completedAt: new Date() } : null,
        }));
        return;
      }

      while (get().pauseRequested) {
        set((s) => ({
          job: s.job ? { ...s.job, status: 'paused' as BulkExportJobStatus } : null,
        }));
        await new Promise((r) => setTimeout(r, 500));
        if (get().cancelRequested) {
          set((s) => ({
            job: s.job ? { ...s.job, status: 'cancelled' as BulkExportJobStatus, completedAt: new Date() } : null,
          }));
          return;
        }
      }

      set((s) => ({
        job: s.job ? { ...s.job, status: 'running' as BulkExportJobStatus } : null,
      }));

      const target = selectedTargets[i];

      updateItem(set, i, {
        status: 'rendering',
        percent: 5,
        message: `Fetching source data for ${target.schoolName}...`,
      });

      try {
        let sourceData = sourceDataCacheByBrand[target.brandId];
        if (!sourceData) {
          sourceData = await fetchMappingSourceData(
            target.brand,
            canvasState.elements,
            elementMappings
          );
          sourceDataCacheByBrand[target.brandId] = sourceData;
        }

        updateItem(set, i, { percent: 15, message: `Resolving brand data for ${target.schoolName}...` });

        const resolvedElements = resolveElementsForBrand(
          canvasState.elements,
          elementMappings,
          target.brand,
          sourceData
        );

        updateItem(set, i, { percent: 30, message: 'Running export pipeline...' });

        const school = schools.find((s) => s.id === target.schoolId);
        const schoolId = school?.legacy_id || target.subdomain || UNBRANDED_LEGACY_ID;

        const stemNoExt = generatePacketFilename(schoolId, activeProject.name, storeId);
        const scaleSuffix = getScaleSuffix(config.scale);
        const baseName = `${stemNoExt}${scaleSuffix}`;

        const imageExt = config.format === 'jpeg' ? 'jpg' : config.format;
        const backgroundImageFilename = `${baseName}.${imageExt}`;
        const overlayFilename = `${stemNoExt}.txt`;

        const result = await runExportPipeline(
          resolvedElements,
          activeProject.id,
          activeProject.name,
          canvasState.dimensions.width,
          canvasState.dimensions.height,
          config,
          (percent, message) => {
            updateItem(set, i, { percent: 30 + percent * 0.6, message });
          },
          undefined,
          canvasState.backgroundColor,
          schoolId,
          buttonUrls,
          {
            packetFilenameOverride: stemNoExt,
            backgroundImageFilenameOverride: backgroundImageFilename,
          }
        );

        if (!result.success || !result.outputs) {
          throw new Error(result.error || 'Export pipeline failed');
        }

        if (result.outputs.backgroundBlob) {
          zipFiles.push({
            name: backgroundImageFilename,
            blob: result.outputs.backgroundBlob,
          });
        }
        if (result.outputs.compositeBlob) {
          zipFiles.push({
            name: `${stemNoExt}_composite${scaleSuffix}.${imageExt}`,
            blob: result.outputs.compositeBlob,
          });
        }
        if (result.outputs.overlayBlob) {
          zipFiles.push({
            name: overlayFilename,
            blob: result.outputs.overlayBlob,
          });
        }

        updateItem(set, i, { status: 'complete', percent: 100, message: 'Complete', outputs: result.outputs });
        set((s) => ({
          job: s.job ? { ...s.job, completedCount: s.job.completedCount + 1 } : null,
        }));
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Unknown error';
        updateItem(set, i, { status: 'failed', percent: 100, message: 'Failed', error: errorMsg });
        set((s) => ({
          job: s.job ? { ...s.job, failedCount: s.job.failedCount + 1 } : null,
        }));
      }
    }

    let zipBlobUrl: string | undefined;
    if (zipFiles.length > 0) {
      try {
        const JSZip = (await import('jszip')).default;
        const zip = new JSZip();
        for (const file of zipFiles) {
          zip.file(file.name, file.blob);
        }
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        zipBlobUrl = URL.createObjectURL(zipBlob);

        const zipPath = `${activeProject.id}/${jobId}/bulk-export.zip`;
        await supabase.storage.from(BULK_BUCKET).upload(zipPath, zipBlob, {
          contentType: 'application/zip',
          cacheControl: '3600',
          upsert: false,
        });
      } catch (err) {
        console.error('[BulkExport] ZIP generation failed:', err);
      }
    }

    const finalState = get();
    const finalJob = finalState.job;
    if (!finalJob) return;

    const finalStatus: BulkExportJobStatus =
      finalJob.failedCount === finalJob.totalCount ? 'failed' : 'complete';

    set({
      job: {
        ...finalJob,
        status: finalStatus,
        completedAt: new Date(),
        zipBlobUrl,
      },
    });

    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;
      if (userId) {
        await supabase.from('cs_bulk_exports').insert({
          id: jobId,
          project_id: activeProject.id,
          template_id: null,
          created_by: userId,
          preset_id: selectedPreset.id,
          export_config: config,
          filename_pattern: { template: 'schoolId_image-title_storeId', separator: '_' },
          total_brands: finalJob.totalCount,
          completed_brands: finalJob.completedCount,
          failed_brands: finalJob.failedCount,
          zip_url: zipBlobUrl || null,
          status: finalStatus === 'failed' ? 'failed' : finalJob.failedCount > 0 ? 'partial' : 'complete',
          error_message: null,
          metadata: {
            preset_label: selectedPreset.label,
            element_count: canvasState.elements.length,
            store_id: storeId,
            filename_convention: 'schoolId_image-title_storeId',
            quality_override: qualityOverride,
            scale_override: scaleOverride,
          },
        });
      }
    } catch (err) {
      console.error('[BulkExport] Failed to save record:', err);
    }
  },

  pauseExport: () => set({ pauseRequested: true }),

  resumeExport: () => set({ pauseRequested: false }),

  cancelExport: () => set({ cancelRequested: true }),

  reset: () =>
    set((s) => ({
      job: null,
      selectedTargets: [],
      cancelRequested: false,
      pauseRequested: false,
      // Preserve preset/overrides/storeId/customPresets between runs so designers
      // don't have to reconfigure every time they open the dialog.
      selectedPreset: s.selectedPreset,
      qualityOverride: s.qualityOverride,
      scaleOverride: s.scaleOverride,
      storeId: s.storeId,
      customPresets: s.customPresets,
      customPresetsLoaded: s.customPresetsLoaded,
      prefsLoaded: s.prefsLoaded,
      filenamePattern: { ...DEFAULT_FILENAME_PATTERN },
    })),
}));

function updateItem(
  set: (fn: (s: BulkExportState) => Partial<BulkExportState>) => void,
  index: number,
  updates: Partial<BulkExportItemProgress>
) {
  set((s) => {
    if (!s.job) return {};
    const items = [...s.job.items];
    items[index] = { ...items[index], ...updates };
    return { job: { ...s.job, items } };
  });
}
